/**
 * Permit jurisdictions — the metro cities that issue install permits (the
 * "Jurisdiction" picklist on a job's permit). Admin-managed (add / rename /
 * archive) so the office runs it without code edits. Seeded from the
 * After-Install packet city docs; this list also becomes the per-city home the
 * CX-01 after-install inspection instructions hang off.
 *
 * The active-only dropdown consumer lives in jobs.ts (useJurisdictions, keyed
 * ["jurisdictions"]); these admin hooks read ALL rows (["all_jurisdictions"])
 * and invalidate both so the permit picker refreshes after an edit.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface JurisdictionRow {
  id: string;
  name: string;
  active: boolean;
  sort: number | null;
}

/** All jurisdictions incl. archived — for the Settings admin. */
export function useAllJurisdictions() {
  return useQuery({
    queryKey: ["all_jurisdictions"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<JurisdictionRow[]> => {
      const { data, error } = await supabase
        .from("pegasus_jurisdiction")
        .select("id, name, active, sort")
        .order("name");
      if (error) throw error;
      return (data ?? []) as JurisdictionRow[];
    },
  });
}

function invalidateJurisdictions(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["all_jurisdictions"] });
  qc.invalidateQueries({ queryKey: ["jurisdictions"] }); // the permit dropdown
}

export function useAddJurisdiction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const n = name.trim();
      if (!n) throw new Error("Name the city first");
      const { error } = await supabase.from("pegasus_jurisdiction").insert({ name: n });
      if (error) {
        if (error.message.toLowerCase().includes("duplicate"))
          throw new Error("That city is already on the list");
        throw error;
      }
    },
    onSuccess: () => invalidateJurisdictions(qc),
  });
}

export function useUpdateJurisdiction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; name?: string; active?: boolean }) => {
      const { id, ...rest } = v;
      const patch: { name?: string; active?: boolean } = { ...rest };
      if (patch.name !== undefined) {
        patch.name = patch.name.trim();
        if (!patch.name) throw new Error("Name can't be blank");
      }
      const { error } = await supabase.from("pegasus_jurisdiction").update(patch).eq("id", id);
      if (error) {
        if (error.message.toLowerCase().includes("duplicate"))
          throw new Error("That city is already on the list");
        throw error;
      }
    },
    onSuccess: () => invalidateJurisdictions(qc),
  });
}
